import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { registryServiceURL } from '../../../redux/services/defines';
import {
  getAuthToken,
  getRefreshToken,
  getRememberMePreference,
  setAuthToken,
} from './storage';

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  companyID: string;
  role: 'admin' | 'user';
  isSuperAdmin: boolean;
  isActive: boolean;
  emailVerified: boolean;
  onboardingCompleted: boolean;
  companyName?: string;
  jobTitle?: string;
  companySize?: string;
  industry?: string;
  useCase?: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
}

type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

interface SetupInput {
  username: string;
  email: string;
  fullName: string;
  password: string;
  companyName?: string;
}

interface AuthContextValue {
  status: AuthStatus;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsSetup: boolean;
  user: AuthUser | null;
  login: (identifier: string, password: string, rememberMe?: boolean) => Promise<void>;
  /** @deprecated Setup is handled via main-service. This is a no-op. */
  setup: (input: SetupInput) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  rememberMe: boolean;
  setRememberMe: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextValue>({
  status: 'loading',
  isLoading: true,
  isAuthenticated: false,
  needsSetup: false,
  user: null,
  login: async () => {
    throw new Error('Auth context not initialized');
  },
  setup: async () => {
    throw new Error('Setup is no longer supported. Use main-service sign-up.');
  },
  logout: async () => {
    throw new Error('Auth context not initialized');
  },
  refresh: async () => {
    throw new Error('Auth context not initialized');
  },
  rememberMe: false,
  setRememberMe: () => {},
});

const parseJson = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const resolveErrorMessage = (payload: any, fallback: string): string => {
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  if (Array.isArray(payload?.message) && payload.message[0]) {
    return String(payload.message[0]);
  }
  return fallback;
};

/** Attempt a silent token refresh. Returns new accessToken or null. */
async function attemptTokenRefresh(refreshToken: string): Promise<{ token: string; refreshToken?: string; user: AuthUser } | null> {
  try {
    const response = await fetch(`${registryServiceURL}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return null;
    const payload = await parseJson(response);
    if (!payload?.token) return null;
    return payload as { token: string; refreshToken?: string; user: AuthUser };
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [rememberMe, setRememberMe] = useState<boolean>(() => getRememberMePreference());

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const token = getAuthToken();
      if (!token) {
        setUser(null);
        setStatus('unauthenticated');
        return;
      }

      // Try to fetch current user profile
      const meResponse = await fetch(`${registryServiceURL}/v1/auth/me`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (meResponse.ok) {
        const mePayload = (await parseJson(meResponse)) as AuthUser;
        setUser(mePayload);
        setStatus('authenticated');
        return;
      }

      // Token expired — try refresh
      if (meResponse.status === 401) {
        const storedRefreshToken = getRefreshToken();
        if (storedRefreshToken) {
          const refreshResult = await attemptTokenRefresh(storedRefreshToken);
          if (refreshResult) {
            setAuthToken(refreshResult.token, rememberMe, refreshResult.refreshToken);
            setUser(refreshResult.user);
            setStatus('authenticated');
            return;
          }
        }
      }

      // Could not authenticate
      setAuthToken(null);
      setUser(null);
      setStatus('unauthenticated');
    } catch {
      setAuthToken(null);
      setUser(null);
      setStatus('unauthenticated');
    }
  }, [rememberMe]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (identifier: string, password: string, remember: boolean = rememberMe) => {
      const response = await fetch(`${registryServiceURL}/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password, rememberMe: remember }),
      });

      const payload = await parseJson(response);
      if (!response.ok) {
        throw new Error(resolveErrorMessage(payload, 'Login failed'));
      }

      setRememberMe(remember);
      setAuthToken(payload.token, remember, payload.refreshToken);
      setUser(payload.user as AuthUser);
      setStatus('authenticated');
    },
    [rememberMe],
  );

  const logout = useCallback(async () => {
    const token = getAuthToken();
    if (token) {
      try {
        await fetch(`${registryServiceURL}/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
      } catch {
        // Ignore API errors and force local logout
      }
    }
    setAuthToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setup = useCallback(async (_input: SetupInput) => {
    throw new Error('Setup is no longer supported. Users are managed via main-service.');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      needsSetup: false, // setup is handled via main-service
      user,
      login,
      setup,
      logout,
      refresh,
      rememberMe,
      setRememberMe,
    }),
    [status, user, login, setup, logout, refresh, rememberMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
